/**
 * The console's navigation, as data, and the labels its addresses are known by.
 *
 * One table, read by two things that must not disagree: the sidebar, which
 * decides what a person can see a link to, and the breadcrumb, which names
 * where they are. Written twice, the two drift within a week and the symptom is
 * a page whose sidebar entry says "Insight" while the bar above it says
 * "Insights", which reads as two different places.
 *
 * The permission on each entry is the same one `routes.tsx` guards the address
 * with, and that pairing is the point. A link a role cannot follow is worse
 * than no link: somebody clicks it, is refused, and now has to work out whether
 * the refusal is the bug or the link is. The route guard is still the thing
 * that decides, and the server is still the thing that enforces; this only
 * decides what is worth showing.
 *
 * `null` means everybody signed in. Two entries carry it, and both are correct:
 * the dashboard is the address the root redirects to, so a role that could not
 * see it would land on a refusal every time it signed in, and settings holds
 * this person's own scheme and density, which belong to no role at all.
 */

import type { Permission } from '@/store/permissions'

export interface NavEntry {
  to: string
  label: string
  /** The icon's name in `components/icons/Icons.tsx`, resolved by the sidebar. */
  icon: NavIconName
  /** What a role must hold to be shown this. `null` is everybody signed in. */
  permission: Permission | null
  /** Matches only this exact path, for an entry whose section has children. */
  end?: boolean
}

export interface NavSection {
  /**
   * Optional, and the first section deliberately has none. A label above the
   * first item in a list of four sections reads as a heading for all of them.
   */
  label?: string
  entries: readonly NavEntry[]
}

export type NavIconName =
  | 'dashboard'
  | 'projects'
  | 'conversations'
  | 'insights'
  | 'cases'
  | 'broadcasts'
  | 'tasks'
  | 'reports'
  | 'team'
  | 'audit'
  | 'settings'

/*
 * The order follows the platform model's own shape rather than an alphabet:
 * what is happening now, then the programme that shapes it, then the work that
 * comes out of it, then the organization around all three.
 *
 * Cases and field tasks are gated on the assigned-only permission, which is the
 * weaker of each pair and matches `routes.tsx`. A field agent has to reach
 * their own caseload; the server decides which rows come back. Gating the nav
 * on `cases.read` would hide from an agent the only screen their role exists to
 * use.
 */
export const NAVIGATION: readonly NavSection[] = [
  {
    entries: [
      { to: '/dashboard', label: 'Dashboard', icon: 'dashboard', permission: null },
    ],
  },
  {
    label: 'Programme',
    entries: [
      { to: '/projects', label: 'Projects', icon: 'projects', permission: 'projects.read' },
      {
        to: '/conversations',
        label: 'Conversations',
        icon: 'conversations',
        permission: 'conversations.readAggregate',
      },
      { to: '/insights', label: 'Insight', icon: 'insights', permission: 'insights.read' },
    ],
  },
  {
    label: 'Work',
    entries: [
      { to: '/cases', label: 'Cases', icon: 'cases', permission: 'cases.readAssigned' },
      { to: '/broadcasts', label: 'Broadcasts', icon: 'broadcasts', permission: 'broadcasts.read' },
      { to: '/tasks', label: 'Field tasks', icon: 'tasks', permission: 'tasks.readAssigned' },
    ],
  },
  {
    label: 'Organization',
    entries: [
      { to: '/reports', label: 'Reports', icon: 'reports', permission: 'reports.export' },
      { to: '/team', label: 'Team', icon: 'team', permission: 'staff.read' },
      { to: '/audit', label: 'Audit', icon: 'audit', permission: 'audit.read' },
      { to: '/settings', label: 'Settings', icon: 'settings', permission: null },
    ],
  },
]

/**
 * What the second segment of an address is called, for the breadcrumb.
 *
 * Only the segments that are a word rather than an identifier are here. A
 * project id has no name the shell knows, and inventing one is the failure this
 * map is shaped to avoid: see `breadcrumbs.ts` for what happens instead.
 */
const SUB_SECTION_LABELS: Readonly<Record<string, string>> = {
  new: 'New',
  gaps: 'Gaps',
  topics: 'Topics',
  rumors: 'Rumors',
  knowledge: 'Knowledge',
  members: 'Members',
  codes: 'Enrolment codes',
  indicators: 'Indicators',
  settings: 'Settings',
}

/** The section an address belongs to, or null for an address in no section. */
export function sectionFor(pathname: string): NavEntry | null {
  const first = pathname.split('/').filter(Boolean)[0]
  if (first === undefined) return null

  for (const section of NAVIGATION) {
    for (const entry of section.entries) {
      if (entry.to === `/${first}`) return entry
    }
  }
  return null
}

export function subSectionLabel(segment: string): string | null {
  return SUB_SECTION_LABELS[segment] ?? null
}
